import { useState, useEffect } from 'react';
import { useOffline } from '../../context/OfflineContext';
import { FiWifiOff, FiWifi, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';

const OfflineIndicator = () => {
  const { isOnline, isSyncing, pendingOperations, syncErrors, forceSync } = useOffline();
  const [showDetails, setShowDetails] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  // Detectar cuando se recupera la conexión para mostrar notificación
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline && isOnline) {
      // Se recuperó la conexión
      setWasOffline(false);
      // Mostrar indicador brevemente
      if (pendingOperations > 0) {
        setShowDetails(true);
        setTimeout(() => setShowDetails(false), 5000);
      }
    }
  }, [isOnline, wasOffline, pendingOperations]);

  const handleForceSync = async () => {
    try {
      await forceSync();
    } catch (error) {
      console.error('Error al forzar sincronización:', error);
    }
  };

  // No mostrar nada si estamos online y no hay operaciones pendientes
  if (isOnline && pendingOperations === 0 && !isSyncing && !showDetails) {
    return null;
  }

  // Indicador cuando está offline
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white px-4 py-2 z-50 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-2">
            <FiWifiOff className="h-5 w-5 animate-pulse" />
            <span className="text-sm font-medium">Sin conexión</span>
            {pendingOperations > 0 && (
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                {pendingOperations} {pendingOperations === 1 ? 'operación' : 'operaciones'} pendiente{pendingOperations !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs underline hover:no-underline"
          >
            {showDetails ? 'Ocultar' : 'Ver más'}
          </button>
        </div>

        {showDetails && pendingOperations > 0 && (
          <div className="mt-2 text-xs max-w-7xl mx-auto">
            <p>Los cambios se guardarán localmente y se sincronizarán cuando se recupere la conexión.</p>
          </div>
        )}
      </div>
    );
  }

  // Indicador cuando está sincronizando
  if (isSyncing && pendingOperations > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-blue-500 text-white px-4 py-2 z-50 shadow-lg">
        <div className="flex items-center justify-center space-x-2 max-w-7xl mx-auto">
          <FiRefreshCw className="h-5 w-5 animate-spin" />
          <span className="text-sm font-medium">
            Sincronizando {pendingOperations} {pendingOperations === 1 ? 'operación' : 'operaciones'}...
          </span>
        </div>
      </div>
    );
  }

  // Indicador cuando hay operaciones pendientes pero está online
  if (pendingOperations > 0 && showDetails) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-orange-500 text-white px-4 py-2 z-50 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-2">
            <FiAlertCircle className="h-5 w-5" />
            <span className="text-sm font-medium">
              {pendingOperations} {pendingOperations === 1 ? 'operación' : 'operaciones'} pendiente{pendingOperations !== 1 ? 's' : ''} de sincronización
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleForceSync}
              className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded transition-colors"
            >
              Sincronizar ahora
            </button>
            <button
              onClick={() => setShowDetails(false)}
              className="text-xs underline hover:no-underline"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Indicador cuando hay errores de sincronización
  if (syncErrors.length > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 bg-red-500 text-white px-4 py-2 z-50 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center space-x-2">
            <FiAlertCircle className="h-5 w-5" />
            <span className="text-sm font-medium">
              Error al sincronizar {syncErrors.length} {syncErrors.length === 1 ? 'operación' : 'operaciones'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleForceSync}
              className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OfflineIndicator;
